/**
 * @fileoverview Helper class that handles loading and dealing with auxiliary data files for ny noir datasets (blocks, locs, pcatg, etc.)
 */


import { loadJsonDataFromPath, fixRelativeChildPathWithParentFilePath, jrPathtoDirectoryOfFile, appendCacheBuster, getOwnValue } from '../utils/jrfuncs.js';
import { geoJsonExtractFeatures } from '../utils/jrgeofuncs.js';
import { isPointInPolygonGeometry, distanceToPolygonGeometry, distanceSquaredToCoords } from '../utils/jrgeofuncs.js';
import { DEBUG_CBM, DEBUG_SIMULATE_NETWORK_DELAY_MS, MAP_MODES, RASTER_POI_COORDS } from '../constants.js';


/**
 * Manages auxiliary data related to the map (blocks, locations, categories, yellow page ads).
 * @class
 */
export class AuxDataManager {
    /**
     * Creates a new AuxDataManager instance.
     * @param {Object} mapApp - The MapApp instance
     * @param {string} basePath - Base path for resolving relative data file URLs
     * @param {Object} auxDataConfig - Configuration object with data source definitions
     */
    constructor(mapApp, basePath, auxDataConfig) {
        this.mapApp = mapApp;
        //
        this.basePath = basePath;
        // a world config with no auxData section has none of it: every loader reads a missing key as
        // "not configured" (they used to fail the world load with a TypeError on undefined)
        this.auxDataConfig = auxDataConfig || {};
        //
        this.pcatgdict = null;
        this.blockData = null;
        this.locData = null;
        this.regionData = null;
        this.iconMapData = null;
        //
        this.yellowAdBasePath = null;
        this.yellowAdUsageDict = null;
        //
        this.otherImagesBasePath = null;
        //
        this.specialYellowData = null;
        //
        this.regionNeighborData = {};
    }


    // ==========================================
    // LOGGING HELPERS
    // ==========================================

    /**
     * Adds an info-level log message via MapApp.
     * @param {string} message - The log message text
     */
    addMessage(message) {
        this.mapApp.addMessage(message);
    }

    /**
     * Adds a warning-level log message via MapApp.
     * @param {string} message - The warning message text
     */
    addWarning(message) {
        this.mapApp.addWarning(message);
    }

    /**
     * Adds an error-level log message via MapApp.
     * @param {string} message - The error message text
     */
    addError(message) {
        this.mapApp.addError(message);
    }


    // ==========================================
    // ACCESSORS
    // ==========================================

    /**
     * Gets the place category dictionary.
     * @returns {Object|null} Category dictionary or null if not loaded
     */
    getPcatgdict() {
        return this.pcatgdict;
    }

    /**
     * Gets the yellow pages advertisement usage dictionary.
     * @returns {Object|null} Yellow ad usage dictionary or null if not loaded
     */
    getYellowAdUsageDict() {
        return this.yellowAdUsageDict;
    }


    /**
     * Gets the yellow pages advertisement for a name or null.
     * @returns {Object|null} Yellow ad usage dictionary or null
     */
    getYellowAdUsageDictForName(name) {
        if (!this.yellowAdUsageDict) {
            return null;
        }
        return this.yellowAdUsageDict[name]
    }


    /**
     * Gets the base path for yellow pages advertisement images.
     * @returns {string|null} Base path for ad images or null if not loaded
     */
    getYellowAdBasePath() {
        return this.yellowAdBasePath;
    }


    /**
     * Gets the base URL for the "otherImages" directory configured under
     * auxData.otherImages.url in the world config. Used as a prefix for
     * relative image paths referenced from data files (e.g. specialYellow
     * contact card images).
     * @returns {string|null} Base path or null if not configured
     */
    getOtherImagesBasePath() {
        return this.otherImagesBasePath;
    }


    /**
     * Resolves a relative image path against the otherImages base URL.
     * If no base is configured the relPath is returned unchanged so callers
     * still get something usable (the browser will resolve it relative to
     * the current document).
     * @param {string} relPath - Image path relative to the otherImages directory
     * @returns {string|null} Full URL, the unchanged relPath, or null if relPath is empty
     */
    computeUrlForOtherImage(relPath) {
        if (!relPath) {
            return null;
        }
        if (!this.otherImagesBasePath) {
            return relPath;
        }
        return this.otherImagesBasePath + '/' + relPath;
    }

    /**
     * Gets the region data with boundaries and labels.
     * @returns {Array|null} Region data array or null if not loaded
     */
    getRegionData() {
        return this.regionData;
    }


    /**
     * Gets the raw specialYellow data dictionary keyed by list id.
     * Each entry has the shape { label, items } where items is the raw array
     * of stub objects loaded from disk. Resolution against the POI database
     * happens later in MapApp.createSpecialYellowData().
     * @returns {Object|null} Raw specialYellow data or null if not loaded
     */
    getRawSpecialYellowData() {
        return this.specialYellowData;
    }


    /**
     * Gets an OpenLayers geometry object for a region by its ID.
     * @param {string} regionId - The region identifier (jregion field value)
     * @returns {ol.geom.Polygon|ol.geom.MultiPolygon|null} OpenLayers geometry or null if not found
     */
    getRegionGeometryById(regionId) {
        if (!this.regionData) {
            return null;
        }

        const selectedRegionRow = this.regionData.find(r => r.jregion === regionId);
        if (!selectedRegionRow || !selectedRegionRow.geometry) {
            return null;
        }

        const geometry = selectedRegionRow.geometry;
        if (geometry.type === 'Polygon') {
            return new ol.geom.Polygon(geometry.coordinates);
        } else if (geometry.type === 'MultiPolygon') {
            return new ol.geom.MultiPolygon(geometry.coordinates);
        }

        return null;
    }




    // ==========================================
    // LOADERS
    // ==========================================


    /**
     * Loads all auxiliary data sources (pcatg, yellow ads, blocks, locations).
     * @async
     * @returns {Promise<void>}
     */
    async loadAll() {
        // we are not going to move all of these to THIS function:
        // Load pcatg dictionary
        await this.loadPcatgData();

        // Load yellowAdDict dictionary
        await this.loadYellowAdData();

        // Resolve the otherImages base URL (no fetch — it's just a directory)
        this.resolveOtherImagesBasePath();

        // Load blockData and LocData dictionaries
        await this.loadBlockData();
        await this.loadLocData();
        await this.loadRegionData();
        await this.loadIconMapData();

        // Load special yellow page lists (curated entries appended to the Yellow dropdown)
        await this.loadSpecialYellowData();
    }



    /**
     * Loads the place category (pcatg) dictionary from the configured URL.
     * The dictionary maps category codes to display names and is used to populate
     * the Yellow Pages dropdown.
     * @async
     * @private
     */
    async loadPcatgData() {
        //console.log(`CBM: In AuxDataManger LoadAll - loadPcatgData`);
        const result = await this.loadJsonDataForAuxKey('pcatg', true);
        if (result) {
            this.pcatgdict = result.data;
        }
    }


    /**
     * Loads the yellow ad configuration
     * @async
     * @private
     */
    async loadYellowAdData() {
        //console.log(`CBM: In AuxDataManger LoadAll - loadYellowAdData`);
        const result = await this.loadJsonDataForAuxKey('yellowAdUsage', false);
        if (result) {
            this.yellowAdUsageDict = result.data;
            this.yellowAdBasePath = jrPathtoDirectoryOfFile(result.url);
        }
    }


    /**
     * Resolves the otherImages base URL from the world config. Unlike the
     * other aux loaders this fetches nothing — auxData.otherImages.url
     * points at a directory, not a JSON file. The resolved URL is later
     * combined with relative image paths via computeUrlForOtherImage().
     * @private
     */
    resolveOtherImagesBasePath() {
        const datumConfig = this.auxDataConfig['otherImages'];
        if (!datumConfig || !datumConfig.url) {
            return;
        }
        const url = fixRelativeChildPathWithParentFilePath(this.basePath, datumConfig.url);
        if (!url) {
            this.addError('No URL specified in otherImages configuration.');
            return;
        }
        this.otherImagesBasePath = url;
    }


    /**
     * Loads block Data
     * @async
     * @private
     */
    async loadBlockData() {
        //console.log(`CBM: In AuxDataManger LoadAll - loadBlockData`);
        const configKey = 'blockData';
        const result = await this.loadJsonDataForAuxKey(configKey, false);
        if (result) {
            const fieldsToKeep = ['jregion', 'blockid', 'jhidden'];
            this.blockData = geoJsonExtractFeatures(result.data, fieldsToKeep, true, this.auxDataConfig[configKey]['projection']);
        }
    }




    /**
     * Loads loc Data
     * @async
     * @private
     */
    async loadLocData() {
        //console.log(`CBM: In AuxDataManger LoadAll - loadLocData`);
        const configKey = 'locData';
        const result = await this.loadJsonDataForAuxKey(configKey, false);
        if (result) {
            const fieldsToKeep = ['jregion', 'blockid', 'jlabel', 'jhidden'];
            this.locData = geoJsonExtractFeatures(result.data, fieldsToKeep, true, this.auxDataConfig[configKey]['projection']);
        }
    }


    /**
     * Loads region Data
     * @async
     * @private
     */
    async loadRegionData() {
        //console.log(`CBM: In AuxDataManger LoadAll - loadRegionData`);
        const configKey = 'regionData';
        const result = await this.loadJsonDataForAuxKey(configKey, false);
        if (result) {
            const fieldsToKeep = ['jregion', 'jregionsNear', 'jlabel', 'jlabel2'];
            this.regionData = geoJsonExtractFeatures(result.data, fieldsToKeep, true, this.auxDataConfig[configKey]['projection']);
            this.applyRasterCoordTransformToRegions();
        }
        // preprocess
        this.buildRegionNeighborData();
    }


    /**
     * Transforms region polygon coordinates for raster mode maps.
     * Converts from normalized (0,0 top-left .. 1,1 bottom-right) to the
     * internal raster pixel system (x: 0..imageWidth, y: -imageHeight..0).
     * Mirrors the POI transform in PoiDataManager.applyRasterCoordTransform().
     * @private
     */
    applyRasterCoordTransformToRegions() {
        const worldConfig = this.mapApp.worldConfig;
        if (!worldConfig || worldConfig.mapMode !== MAP_MODES.RASTER) {
            return;
        }
        const rasterConfig = worldConfig.rasterConfig;
        if (!rasterConfig || !rasterConfig.poiCoords) {
            return;
        }
        if (!this.regionData) {
            return;
        }

        const poiCoords = rasterConfig.poiCoords;
        const imageWidth = rasterConfig.imageWidth;
        const imageHeight = rasterConfig.imageHeight;

        for (const region of this.regionData) {
            if (!region.geometry || !region.geometry.coordinates) {
                continue;
            }
            const geomType = region.geometry.type;
            const coords = region.geometry.coordinates;

            const transformPair = (pair) => {
                let x = pair[0];
                let y = pair[1];
                if (poiCoords === RASTER_POI_COORDS.NORMALIZED) {
                    x = x * imageWidth;
                    y = y * imageHeight;
                }
                pair[0] = x;
                pair[1] = -y;
            };

            if (geomType === 'Polygon') {
                for (const ring of coords) {
                    for (const pair of ring) {
                        transformPair(pair);
                    }
                }
            } else if (geomType === 'MultiPolygon') {
                for (const polygon of coords) {
                    for (const ring of polygon) {
                        for (const pair of ring) {
                            transformPair(pair);
                        }
                    }
                }
            }
        }
    }


    /**
     * Loads specialYellow data — curated lists appended to the Yellow Pages
     * dropdown under a SPECIAL group. The auxData.specialYellow config is a
     * dictionary of { listId: { url, label } } entries; each entry's url
     * points at a JSON file containing { label, items } where items is an
     * array of bookmark-style stubs.
     * Items are stored raw here; MapApp.createSpecialYellowData() resolves
     * stubs against the POI database after data load completes.
     * @async
     * @private
     */
    async loadSpecialYellowData() {
        const configKey = 'specialYellow';
        const groupConfig = this.auxDataConfig[configKey];
        if (!groupConfig || typeof groupConfig !== 'object') {
            return;
        }

        const result = {};
        for (const [listId, entryConfig] of Object.entries(groupConfig)) {
            if (!entryConfig || !entryConfig.url) {
                this.addWarning(`specialYellow entry "${listId}" has no url; skipping.`);
                continue;
            }
            const url = fixRelativeChildPathWithParentFilePath(this.basePath, entryConfig.url);
            const fetchUrl = appendCacheBuster(url, this.mapApp.getWorldCacheBusterVersion());
            if (DEBUG_CBM) {
                console.debug(`DEBUG: CasebookMap Debug: Loading specialYellow "${listId}" data from: ${fetchUrl}.`);
            }
            try {
                const data = await loadJsonDataFromPath(fetchUrl, `Loading specialYellow list "${listId}".`, DEBUG_SIMULATE_NETWORK_DELAY_MS);
                const label = entryConfig.label || (data && data.label) || listId;
                const items = (data && Array.isArray(data.items)) ? data.items : [];
                result[listId] = { label, items };
            } catch (error) {
                this.addError(`Failed to load specialYellow list "${listId}" from "${fetchUrl}": ${error}.`);
            }
        }
        this.specialYellowData = result;
    }


    /**
     * Loads iconMap Data
     * @async
     * @private
     */
    async loadIconMapData() {
        //console.log(`CBM: In AuxDataManger LoadAll - loadIconMapData`);
        const configKey = 'iconMap';
        const result = await this.loadJsonDataForAuxKey(configKey, false);
        // a failed fetch comes back as {data: null} (already reported in the sidebar): keep the
        // site-wide icon map the image helper already loaded rather than failing the whole world
        if (result && result.data) {
            this.iconMapData = result.data;
            // set the icon map override data
            this.mapApp.getImageHelper().loadIconMapFromData(result.data);
        }
    }




    // ==========================================
    // LOADER HELPERS
    // ==========================================


    /**
     * Helper function to load JSON data from a configured source.
     * Handles configuration validation, URL resolution, fetching, and error handling.
     * @async
     * @private
     * @param {string} configKey - The key in auxDataConfig to look up
     * @returns {Promise<{data: any, url: string}|null>} The parsed JSON data and resolved URL, or null if loading failed
     */
    async loadJsonDataForAuxKey(configKey, flagErrorIfMissing) {
        // Check if configuration exists
        if (!this.auxDataConfig[configKey]) {
            if (flagErrorIfMissing) {
                this.addError(`No ${configKey} configured.`);
            }
            return null;
        }

        // Extract URL from configuration
        const datumConfig = this.auxDataConfig[configKey];
        // (checked before resolving it: the resolver throws on a missing path, which used to hide
        // this message behind a TypeError and fail the world load)
        let url = datumConfig.url ? fixRelativeChildPathWithParentFilePath(this.basePath, datumConfig.url) : '';
        if (!url) {
            this.addError(`No URL specified in ${configKey} configuration.`);
            return null;
        }

        const fetchUrl = appendCacheBuster(url, this.mapApp.getWorldCacheBusterVersion());

        if (DEBUG_CBM) {
            console.debug(`DEBUG: CasebookMap Debug: Loading AUX "${configKey}" data from: ${fetchUrl}.`);
        }

        try {
            const data = await loadJsonDataFromPath(fetchUrl, `Loading aux data from ${configKey}.`, DEBUG_SIMULATE_NETWORK_DELAY_MS);
            return {data, url};
        }
        catch (error) {
            const errorMessage = `Failed to load "${configKey}" data from "${fetchUrl}": ${error}.`;
            this.addError(errorMessage);
            const data = null;
            return {data, url};
        }
    }






























    // ==========================================
    // Helpers for clicking on map
    // ==========================================


    /**
     * This function will be passed a geo map location by the OpenLayersMapManager class when the user clicks on a point on the map where there is no visible marker.
     * This function will first find a row in this.blockData which has an enclosing Polygon geometry
     * If a row with an enclosing polygon geometry in blockdata was not found, then find the row with the closest polygon to the point.
     * With this blockdata row, we then find the nearest row in this.locData whose blockid matches the blockid of the blockData row.
     * Then return a string which is the locData row jregion field concattenated with the locData row jlabel field
     * If no such locData row was found return ""
     *
     * @param {Array<number>} geoPoint - [x, y] coordinates of the clicked point
     * @returns {string} Region and block label string, or empty string if not found
     */
    identifySelectedRegionBlockStringFromGeoPointViaBlockData(geoPoint, maxDist) {
        if (!this.blockData) {
            // no block data available
            return null;
        }

        const [px, py] = geoPoint;
        let insideGeometry = false;

        // First, try to find a block polygon that contains the point
        let foundBlock = null;
        for (const block of this.blockData) {
            if (block.jhidden) {
                continue;
            }
            if (block.geometry && isPointInPolygonGeometry(px, py, block.geometry)) {
                foundBlock = block;
                insideGeometry = true;
                break;
            }
        }

        // If no enclosing polygon found, find the closest one
        if (!foundBlock) {
            let minDistance = Infinity;
            for (const block of this.blockData) {
                if (block.jhidden) {
                    continue;
                }
                if (block.geometry) {
                    const distance = distanceToPolygonGeometry(px, py, block.geometry);
                    if (distance < minDistance) {
                        minDistance = distance;
                        foundBlock = block;
                    }
                }
            }
        }

        if (!foundBlock) {
            return "";
        }

        // Now find the matching locData row
        if (!this.locData || this.locData.length === 0) {
            return "";
        }

        // Find locData rows that match the blockid
        let foundLoc = null;
        let minLocDistance = Infinity;

        for (const loc of this.locData) {
            if (loc.jhidden) {
                continue;
            }
            if (loc.blockid === foundBlock.blockid) {
                // If there are multiple matching locs, find the nearest one by geometry
                if (loc.geometry) {
                    const distance = distanceSquaredToCoords(px, py, loc.geometry.coordinates);
                    if (distance < minLocDistance) {
                        minLocDistance = distance;
                        foundLoc = loc;
                    }
                } else if (!foundLoc) {
                    foundLoc = loc;
                }
            }
        }

        if (!foundLoc) {
            return "";
        }

        if (!insideGeometry && (minLocDistance > maxDist)) {
            return "";
        }
        // Return concatenated jregion and jlabel
        return `${foundLoc.jregion}${foundLoc.jlabel}`;
    }





    /**
     * Builds a lookup dictionary for region neighbor relationships.
     * Preprocesses regionData for fast neighbor lookups by region code.
     * Called automatically after loading region data.
     * @private
     */
    buildRegionNeighborData() {
        // build region neighbor data for faster access
        // build this.regionNeighborData as a dictionary built from the rows of this.regionData where the key is the jregion field and the value is the jregionsNear field
        this.regionNeighborData = {};
        if (this.regionData) {
            for (const region of this.regionData) {
                if (region.jregion) {
                    this.regionNeighborData[region.jregion] = region.jregionsNear || '';
                }
            }
        }
    }




    /**
     * Gets the list of regions neighboring a given region.
     * Callers must test membership with an exact match (Array.includes): this used to return the
     * comma string itself, and a substring test against it let the SHCD codes E and S match inside
     * EC, SE and SW (nearRegion("SE") listed 1250 places, 827 of them wrong).
     * @param {string} regionTarget - The region code to get neighbors for
     * @param {boolean} flagIncludeTarget - If true, includes the target region in the result
     * @returns {Array<string>} Neighboring region codes (empty for an unknown region)
     */
    getNeighboringRegionList(regionTarget, flagIncludeTarget) {
        // the target comes from the user's query (or the anchor), so an unknown or prototype-named
        // one must read as "no neighbors" (it used to give the list "undefined,X")
        const nearString = getOwnValue(this.regionNeighborData, regionTarget) || '';
        const regionList = String(nearString).split(',').map(code => code.trim()).filter(code => code);
        // add regionTarget itself
        if (flagIncludeTarget && regionTarget) {
            regionList.push(regionTarget);
        }
        return regionList;
    }






}
